import React, { useRef, useState, useEffect, useCallback } from 'react';
import { CardSchema } from 'constants/graphqlTypes';
import { BreathingWrap } from './styled';
import Body from '../Common/Body';

interface AnimatedCardVisualProps {
  card: CardSchema;
  dataTestId?: string;
  colorValue: string;
  pauseAnimation?: boolean;
}

const AnimatedCardVisual: React.FC<AnimatedCardVisualProps> = React.memo(
  ({ card, dataTestId, colorValue, pauseAnimation }) => {
    const [isBreathing, setIsBreathing] = useState(false);
    const [isMoving, setIsMoving] = useState(false);
    const [isRotating, setIsRotating] = useState(false);
    const [isReturning, setIsReturning] = useState(false);
    const cardRef = useRef<HTMLDivElement>(null);
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
    const sequenceRef = useRef<ReturnType<typeof setInterval>>();
    const [cardSize, setCardSize] = useState({ width: 0, height: 0 });
    const [isCardMeasured, setIsCardMeasured] = useState(false);
    const parentRef = useRef<HTMLDivElement>(null);
    const [breathingPhase, setBreathingPhase] = useState<'inhale' | 'hold1' | 'exhale' | 'hold2'>('inhale');
    const breathingTimeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
    const animationTimeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

    // Scroll-tethered animation state
    const [isImageAnimating, setIsImageAnimating] = useState(false);
    const [imageAnimationProgress, setImageAnimationProgress] = useState(0);
    const imageRef = useRef<HTMLDivElement>(null);
    const lastScrollY = useRef(0);
    const isInTriggerZone = useRef(false);

    // iOS-style keyframe interpolation function
    const interpolateFromKeyframes = (progress: number, keyframes: number[]): number => {
      const kfProgress = [0.0, 0.25, 0.5, 0.6, 0.75, 1.0];
      let lowerIndex = 0;
      let upperIndex = 1;
      for (let i = 0; i < kfProgress.length - 1; i += 1) {
        if (progress >= kfProgress[i] && progress <= kfProgress[i + 1]) {
          lowerIndex = i;
          upperIndex = i + 1;
          break;
        }
      }
      if (progress > kfProgress[kfProgress.length - 1]) {
        return keyframes[keyframes.length - 1];
      }
      const lowerProgress = kfProgress[lowerIndex];
      const upperProgress = kfProgress[upperIndex];
      const lowerValue = keyframes[lowerIndex];
      const upperValue = keyframes[upperIndex];
      const t = (progress - lowerProgress) / (upperProgress - lowerProgress);
      return lowerValue + (upperValue - lowerValue) * t;
    };

    useEffect(() => {
      const handleResize = () => {
        if (!parentRef.current) return;
        const rect = parentRef.current.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          setCardSize({ width: rect.width, height: rect.height });
          setIsCardMeasured(true);
        }
      };
      handleResize();
      if (!parentRef.current) return undefined;
      const resizeObserver = new window.ResizeObserver(handleResize);
      resizeObserver.observe(parentRef.current);
      return () => resizeObserver.disconnect();
    }, []);

    const clearBreathingTimeouts = () => {
      breathingTimeoutsRef.current.forEach(clearTimeout);
      breathingTimeoutsRef.current = [];
    };

    const clearAnimationTimeouts = () => {
      animationTimeoutsRef.current.forEach(clearTimeout);
      animationTimeoutsRef.current = [];
    };

    const startBreathingCycle = (onComplete?: () => void): void => {
      clearBreathingTimeouts();
      setBreathingPhase('exhale');
      setIsBreathing(true);
      breathingTimeoutsRef.current.push(
        setTimeout(() => {
          setBreathingPhase('hold2');
          breathingTimeoutsRef.current.push(
            setTimeout(() => {
              setBreathingPhase('inhale');
              breathingTimeoutsRef.current.push(
                setTimeout(() => {
                  setBreathingPhase('hold1');
                  breathingTimeoutsRef.current.push(
                    setTimeout(() => {
                      setIsBreathing(false);
                      if (onComplete) onComplete();
                    }, 0)
                  );
                }, 1650)
              );
            }, 500)
          );
        }, 2250)
      );
    };

    const startAnimationSequence = useCallback((): void => {
      if (pauseAnimation) {
        return;
      }
      clearAnimationTimeouts();
      animationTimeoutsRef.current.push(
        setTimeout(() => {
          startBreathingCycle(() => {
            if (pauseAnimation) {
              return;
            }
            setIsMoving(true);
            animationTimeoutsRef.current.push(
              setTimeout(() => {
                setIsMoving(false);
                setIsRotating(true);
                animationTimeoutsRef.current.push(
                  setTimeout(() => {
                    setIsRotating(false);
                    setIsReturning(true);
                    animationTimeoutsRef.current.push(
                      setTimeout(() => {
                        setIsMoving(false);
                        setIsReturning(false);
                        if (!pauseAnimation) {
                          startAnimationSequence();
                        }
                      }, 500)
                    );
                  }, 1500)
                );
              }, 500)
            );
          });
        }, 1000)
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pauseAnimation]);

    const stopAnimation = useCallback((): void => {
      setIsBreathing(false);
      setIsMoving(false);
      setIsRotating(false);
      setIsReturning(false);
      setBreathingPhase('inhale');
      clearBreathingTimeouts();
      clearAnimationTimeouts();
    }, []);

    useEffect(() => {
      if (pauseAnimation) {
        stopAnimation();
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = undefined;
        }
        if (sequenceRef.current) {
          clearInterval(sequenceRef.current);
          sequenceRef.current = undefined;
        }
        return (): void => {
          // Cleanup function for when pauseAnimation is true
        };
      }

      let debounceTimer: ReturnType<typeof setTimeout>;

      const observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          const { intersectionRatio } = entry;

          // Debounce the intersection observer calls
          clearTimeout(debounceTimer);
          debounceTimer = setTimeout(() => {
            if (intersectionRatio >= 0.7 && isCardMeasured && !pauseAnimation) {
              if (!timeoutRef.current) {
                timeoutRef.current = setTimeout(() => {
                  startAnimationSequence();
                  timeoutRef.current = undefined;
                }, 1000);
              }
            } else {
              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = undefined;
              }
              if (sequenceRef.current) {
                clearInterval(sequenceRef.current);
                sequenceRef.current = undefined;
              }
              if (intersectionRatio < 0.7) {
                stopAnimation();
              }
            }
          }, 100); // 100ms debounce
        },
        {
          threshold: [0.7],
          rootMargin: '10% 0px -10% 0px',
        }
      );
      if (cardRef.current) {
        observer.observe(cardRef.current);
      }

      return (): void => {
        clearTimeout(debounceTimer);
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        if (sequenceRef.current) {
          clearInterval(sequenceRef.current);
        }
        observer.disconnect();
        clearBreathingTimeouts();
        clearAnimationTimeouts();
      };
    }, [isCardMeasured, pauseAnimation, startAnimationSequence, stopAnimation]);

    useEffect(() => {
      const handleScroll = (): void => {
        if (!imageRef.current || !isInTriggerZone.current) return;
        const currentScrollY = window.scrollY;
        lastScrollY.current = currentScrollY;
        const rect = imageRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const triggerStartY = viewportHeight * 0.2;
        const animationEndYPos = viewportHeight * 0.01;
        const currentY = rect.bottom;
        const animationRange = triggerStartY - animationEndYPos;
        const progressInRange = (triggerStartY - currentY) / animationRange;
        const clampedProgress = Math.max(0, Math.min(1, progressInRange));
        setImageAnimationProgress(clampedProgress);
        setIsImageAnimating(clampedProgress > 0 && clampedProgress < 1);
      };
      const imageObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          const { intersectionRatio, boundingClientRect } = entry;
          const viewportHeight = window.innerHeight;
          const triggerStartY = viewportHeight * 0.2;
          isInTriggerZone.current = intersectionRatio > 0 && boundingClientRect.bottom <= triggerStartY;
          if (isInTriggerZone.current) {
            lastScrollY.current = window.scrollY;
            handleScroll();
          } else {
            setIsImageAnimating(false);
            setImageAnimationProgress(0);
          }
        },
        {
          threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
          rootMargin: '20% 0px -1% 0px',
        }
      );
      if (imageRef.current) {
        imageObserver.observe(imageRef.current);
      }
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => {
        imageObserver.disconnect();
        window.removeEventListener('scroll', handleScroll);
      };
    }, []);

    const interpolatedRotation = interpolateFromKeyframes(imageAnimationProgress, [
      -0.0,
      -8.54,
      -25.35,
      -59.88,
      -109.1,
      -120.0,
    ]);
    const interpolatedScale = interpolateFromKeyframes(imageAnimationProgress, [1.0, 0.7, 0.52, 0.44, 0.3, 0.0]);

    return (
      <div ref={parentRef} style={{ perspective: 2000 }}>
        <BreathingWrap
          ref={cardRef}
          isBreathing={isBreathing}
          isMoving={isMoving}
          isRotating={isRotating}
          isReturning={isReturning}
          cardWidth={cardSize.width}
          cardHeight={cardSize.height}
          breathingPhase={breathingPhase}
          bgColor={colorValue}
        >
          <Body
            dataTestId={dataTestId}
            card={card}
            isFirstUp
            imageRef={imageRef}
            imageAnimationProgress={imageAnimationProgress}
            isImageAnimating={isImageAnimating}
            interpolatedRotation={interpolatedRotation}
            interpolatedScale={interpolatedScale}
          />
        </BreathingWrap>
      </div>
    );
  }
);

export default AnimatedCardVisual;
